import { Component, computed, inject, signal } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Scheme, Theming } from '@/app/core/theming';

interface ColorOption  { label: string; value: string; }
interface SchemeOption { label: string; value: Scheme; icon: string; }

@Component({
  selector: 'settings-panel',
  imports: [MatIconButton, MatIcon],
  template: `
    <!-- Botón engrane fijo -->
    <div
      class="fixed right-0 z-90 flex h-10 w-10 cursor-pointer items-center justify-center rounded-l-lg bg-red-600 shadow-lg"
      style="top: 275px"
      (click)="togglePanel()"
    >
      <mat-icon svgIcon="settings" class="text-white !size-5" />
    </div>

    @if (isOpen()) {
      <!-- Backdrop -->
      <div
        class="fixed inset-0 z-40 bg-black/20"
        (click)="closePanel()"
      ></div>

      <!-- Panel -->
      <div class="fixed top-0 right-0 z-50 flex h-full w-80 flex-col overflow-auto bg-white dark:bg-neutral-900 shadow-2xl">

        <!-- Header azul -->
        <div class="flex h-16 min-h-16 flex-row items-center bg-primary px-6 text-white">
          <mat-icon svgIcon="settings" class="text-white !size-6" />
          <div class="ml-3 text-xl font-semibold tracking-tight">Configuración</div>
          <button matIconButton class="ml-auto !text-white" (click)="closePanel()">
            <mat-icon svgIcon="x" class="text-white" />
          </button>
        </div>

        <div class="flex flex-col p-6">

          <!-- ── TEMA ──────────────────────────────────── -->
          <div class="text-sm font-semibold text-neutral-500 dark:text-neutral-400">TEMA</div>
          <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            @for (color of colors; track color.value) {
              <div
                class="flex cursor-pointer items-center justify-center rounded-full px-4 py-3 ring-inset ring-primary hover:opacity-90"
                [class.ring-2]="primaryColor() === color.value"
                [style.background]="primaryColor() === color.value ? color.value + '15' : ''"
                (click)="updateColor(color.value)"
              >
                <div class="h-3 w-3 flex-shrink-0 rounded-full" [style.background]="color.value"></div>
                <div
                  class="ml-2.5 truncate font-medium leading-5 text-sm"
                  [class.text-neutral-400]="primaryColor() !== color.value"
                >{{ color.label }}</div>
              </div>
            }
          </div>

          <hr class="my-6 border-neutral-200 dark:border-neutral-700" />

          <!-- ── ESQUEMA ───────────────────────────────── -->
          <div class="text-sm font-semibold text-neutral-500 dark:text-neutral-400">ESQUEMA</div>
          <div class="mt-4 grid grid-cols-3 gap-3">
            @for (item of schemes; track item.value) {
              <div
                class="flex cursor-pointer items-center rounded-full py-3 pl-4 pr-5 ring-inset ring-primary hover:opacity-90 bg-neutral-100 dark:bg-neutral-800"
                [class.ring-2]="scheme() === item.value"
                (click)="updateScheme(item.value)"
              >
                <mat-icon [svgIcon]="item.icon" class="!size-5" />
                <div
                  class="ml-2 text-sm font-medium leading-5"
                  [class.text-neutral-400]="scheme() !== item.value"
                >{{ item.label }}</div>
              </div>
            }
          </div>

          <hr class="my-6 border-neutral-200 dark:border-neutral-700" />

          <!-- ── DISEÑO ────────────────────────────────── -->
          <div class="text-sm font-semibold text-neutral-500 dark:text-neutral-400">DISEÑO</div>
          <div class="mt-4 grid grid-cols-3 gap-3">

            <!-- Vacío -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('vacio')">
              <div class="flex h-20 flex-col overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'vacio'">
                <div class="flex flex-auto flex-col bg-gray-50 dark:bg-gray-900"></div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'vacio'" [class.text-neutral-500]="design() !== 'vacio'">Vacío</div>
            </div>

            <!-- Clásico -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('clasico')">
              <div class="flex h-20 overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'clasico'">
                <div class="w-8 bg-gray-100 dark:bg-gray-800">
                  <div class="mx-1.5 mt-3 space-y-1">
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                </div>
                <div class="flex flex-auto flex-col border-l">
                  <div class="h-3 bg-gray-100 dark:bg-gray-800">
                    <div class="mr-1.5 flex h-full items-center justify-end">
                      <div class="ml-1 h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                      <div class="ml-1 h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                      <div class="ml-1 h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    </div>
                  </div>
                  <div class="flex flex-auto border-t bg-gray-50 dark:bg-gray-900"></div>
                </div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'clasico'" [class.text-neutral-500]="design() !== 'clasico'">Clásico</div>
            </div>

            <!-- Elegante -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('elegante')">
              <div class="flex h-20 overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'elegante'">
                <div class="w-8 bg-gray-100 dark:bg-gray-800">
                  <div class="mx-1 mt-1 flex items-center">
                    <div class="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="ml-auto h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="ml-0.5 h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                  <div class="mx-auto mt-2.5 h-4 w-4 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  <div class="mx-1 mt-2 space-y-1">
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                </div>
                <div class="flex flex-auto flex-col border-l">
                  <div class="h-3 bg-gray-100 dark:bg-gray-800">
                    <div class="mr-2 flex h-full items-center justify-end">
                      <div class="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    </div>
                  </div>
                  <div class="flex flex-auto border-t bg-gray-50 dark:bg-gray-900"></div>
                </div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'elegante'" [class.text-neutral-500]="design() !== 'elegante'">Elegante</div>
            </div>

            <!-- Compacto -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('compacto')">
              <div class="flex h-20 overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'compacto'">
                <div class="w-5 bg-gray-100 dark:bg-gray-800">
                  <div class="mx-auto mt-2 h-3 w-3 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                  <div class="mt-2 flex w-full flex-col items-center space-y-1">
                    <div class="h-2.5 w-3 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-2.5 w-3 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-2.5 w-3 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                </div>
                <div class="flex flex-auto flex-col border-l">
                  <div class="h-3 bg-gray-100 dark:bg-gray-800">
                    <div class="mr-1.5 flex h-full items-center justify-end">
                      <div class="ml-1 h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                      <div class="ml-1 h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                      <div class="ml-1 h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    </div>
                  </div>
                  <div class="flex flex-auto border-t bg-gray-50 dark:bg-gray-900"></div>
                </div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'compacto'" [class.text-neutral-500]="design() !== 'compacto'">Compacto</div>
            </div>

            <!-- Denso -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('denso')">
              <div class="flex h-20 overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'denso'">
                <div class="w-4 bg-gray-100 dark:bg-gray-800">
                  <div class="mx-auto mt-2 h-2 w-2 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                  <div class="mt-2 flex w-full flex-col items-center space-y-1">
                    <div class="h-2 w-2 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-2 w-2 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-2 w-2 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                </div>
                <div class="flex flex-auto flex-col border-l">
                  <div class="h-3 bg-gray-100 dark:bg-gray-800"></div>
                  <div class="flex flex-auto border-t bg-gray-50 dark:bg-gray-900"></div>
                </div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'denso'" [class.text-neutral-500]="design() !== 'denso'">Denso</div>
            </div>

            <!-- Futurístico -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('futuristico')">
              <div class="flex h-20 overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'futuristico'">
                <div class="w-8 bg-gray-100 dark:bg-gray-800">
                  <div class="flex h-full flex-auto flex-col space-y-1 px-1.5 py-3">
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                    <div class="flex-auto"></div>
                    <div class="h-1 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                </div>
                <div class="flex flex-auto flex-col border-l">
                  <div class="h-3 bg-gray-100 dark:bg-gray-800"></div>
                  <div class="flex flex-auto border-t bg-gray-50 dark:bg-gray-900"></div>
                </div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'futuristico'" [class.text-neutral-500]="design() !== 'futuristico'">Futurístico</div>
            </div>

            <!-- Delgado -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('delgado')">
              <div class="flex h-20 overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'delgado'">
                <div class="w-3 bg-gray-100 dark:bg-gray-800">
                  <div class="mx-auto mt-2 h-1.5 w-1.5 rounded-sm bg-gray-300 dark:bg-gray-700"></div>
                  <div class="mt-2 flex w-full flex-col items-center space-y-1">
                    <div class="h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                </div>
                <div class="flex flex-auto flex-col border-l">
                  <div class="h-3 bg-gray-100 dark:bg-gray-800"></div>
                  <div class="flex flex-auto border-t bg-gray-50 dark:bg-gray-900"></div>
                </div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'delgado'" [class.text-neutral-500]="design() !== 'delgado'">Delgado</div>
            </div>

            <div class="col-span-2"></div>

            <!-- Centrado -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('centrado')">
              <div class="flex h-20 overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'centrado'">
                <div class="mx-2 my-1 flex flex-auto flex-col overflow-hidden rounded-md border">
                  <div class="flex h-3 items-center bg-gray-100 dark:bg-gray-800">
                    <div class="ml-1.5 flex">
                      <div class="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                      <div class="ml-1 h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                      <div class="ml-1 h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    </div>
                    <div class="ml-auto mr-1.5 flex items-center justify-end">
                      <div class="ml-1 h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                      <div class="ml-1 h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    </div>
                  </div>
                  <div class="flex flex-auto border-t bg-gray-50 dark:bg-gray-900"></div>
                </div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'centrado'" [class.text-neutral-500]="design() !== 'centrado'">Centrado</div>
            </div>

            <!-- Empresarial -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('empresarial')">
              <div class="flex h-20 flex-col overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'empresarial'">
                <div class="flex h-3 items-center bg-gray-100 px-2 dark:bg-gray-800">
                  <div class="h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  <div class="ml-auto flex items-center justify-end space-x-1">
                    <div class="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                </div>
                <div class="flex h-3 items-center space-x-1 border-b border-t bg-gray-100 px-2 dark:bg-gray-800">
                  <div class="h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  <div class="h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  <div class="h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                </div>
                <div class="mx-2 my-1 flex flex-auto flex-col overflow-hidden rounded border">
                  <div class="flex flex-auto bg-gray-50 dark:bg-gray-900"></div>
                </div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'empresarial'" [class.text-neutral-500]="design() !== 'empresarial'">Empresarial</div>
            </div>

            <!-- Material -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('material')">
              <div class="flex h-20 flex-col overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'material'">
                <div class="mx-2 my-1 flex flex-auto flex-col overflow-hidden rounded border">
                  <div class="flex h-4 items-center bg-gray-100 px-2 dark:bg-gray-800">
                    <div class="h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="ml-auto flex items-center justify-end space-x-1">
                      <div class="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                      <div class="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    </div>
                  </div>
                  <div class="flex h-2 items-center space-x-1 bg-gray-100 px-2 dark:bg-gray-800">
                    <div class="h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                  <div class="flex flex-auto border-t bg-gray-50 dark:bg-gray-900"></div>
                </div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'material'" [class.text-neutral-500]="design() !== 'material'">Material</div>
            </div>

            <!-- Moderno -->
            <div class="flex cursor-pointer flex-col" (click)="updateDesign('moderno')">
              <div class="flex h-20 flex-col overflow-hidden rounded-md border-2 hover:opacity-80"
                [class.border-primary]="design() === 'moderno'">
                <div class="flex h-4 items-center border-b bg-gray-100 px-2 dark:bg-gray-800">
                  <div class="h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  <div class="ml-2 flex h-3 items-center space-x-1">
                    <div class="h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 w-3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                  <div class="ml-auto flex items-center justify-end space-x-1">
                    <div class="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                    <div class="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></div>
                  </div>
                </div>
                <div class="flex flex-auto flex-col">
                  <div class="flex flex-auto bg-gray-50 dark:bg-gray-900"></div>
                </div>
              </div>
              <div class="mt-2 text-center text-sm font-medium" [class.text-primary]="design() === 'moderno'" [class.text-neutral-500]="design() !== 'moderno'">Moderno</div>
            </div>

          </div>
        </div>
      </div>
    }
  `,
})
export class SettingsPanel {
  private theming = inject(Theming);

  protected isOpen      = signal(false);
  protected scheme      = computed(() => this.theming.scheme());
  protected primaryColor = computed(() => this.theming.colors().primary);
  protected design      = computed(() => this.theming.design());

  protected schemes: SchemeOption[] = [
    { label: 'Auto',   value: 'system', icon: 'sun-moon' },
    { label: 'Oscuro', value: 'dark',   icon: 'moon' },
    { label: 'Claro',  value: 'light',  icon: 'sun' },
  ];

  protected colors: ColorOption[] = [
    { label: 'Default', value: '#4f46e5' },
    { label: 'Brand',   value: '#2563eb' },
    { label: 'Teal',    value: '#0891b2' },
    { label: 'Rose',    value: '#db2777' },
    { label: 'Purple',  value: '#7c3aed' },
    { label: 'Amber',   value: '#d97706' },
    { label: 'Verde',   value: '#16a34a' },
    { label: 'Naranja', value: '#ea580c' },
  ];

  togglePanel() { this.isOpen.update(v => !v); }
  closePanel()  { this.isOpen.set(false); }

  updateScheme(scheme: Scheme) { this.theming.scheme.set(scheme); }
  updateColor(color: string)   { this.theming.colors.set({ ...this.theming.colors(), primary: color }); }
  updateDesign(key: string)    { this.theming.design.set(key); }
}
